<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { definePage } from "unplugin-vue-router/runtime";
import { ITeroService } from "@/api/models/integrations/itero";
import { useQueryClient } from "@tanstack/vue-query";
import { ITeroQueryKeys } from "@/composables/integrations/useIntegrations";
import { useI18n } from "vue-i18n";
import logoITero from "@/assets/iTero.svg";

definePage({
  name: "itero-integration",
  meta: {
    requiresAuth: true,
  },
});

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();
const { t } = useI18n();

const error = ref<string | null>(null);
const success = ref(false);
const loading = ref(false);
const redirectCountdown = ref(5);
let redirectTimer: ReturnType<typeof setInterval> | null = null;

const isProviderError = computed(() => typeof route.query.error === "string");

function parseErrorMessage(err: unknown): string {
  const status = (err as { response?: { status?: number; data?: { detail?: string } } })?.response
    ?.status;
  const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;

  if (detail) {
    return detail;
  }

  switch (status) {
    case 400:
      return t("features.integrations.iteroOAuth.errors.invalidPayload");
    case 403:
      return t("features.integrations.iteroOAuth.errors.invalidState");
    case 502:
      return t("features.integrations.iteroOAuth.errors.providerPayload");
    default:
      return t("features.integrations.iteroOAuth.errors.generic");
  }
}

function startRedirectToSettings() {
  redirectCountdown.value = 5;
  redirectTimer = setInterval(() => {
    redirectCountdown.value -= 1;

    if (redirectCountdown.value <= 0) {
      if (redirectTimer) {
        clearInterval(redirectTimer);
      }
      router.push("/settings/integrations");
    }
  }, 1000);
}

async function submit() {
  loading.value = true;
  error.value = null;
  success.value = false;
  const state = route.query.state as string | undefined;
  const code = route.query.code as string | undefined;
  const providerError = route.query.error as string | undefined;
  const providerErrorDescription = route.query.error_description as string | undefined;

  if (isProviderError.value && providerError) {
    try {
      await ITeroService.oauthCallback(
        {},
        {
          error: providerError,
          error_description: providerErrorDescription,
        }
      );
    } catch (err) {
      error.value = parseErrorMessage(err);
    } finally {
      loading.value = false;
    }

    return;
  }

  if (!state || !code) {
    error.value = t("features.integrations.iteroOAuth.errors.missingStateOrCode");
    loading.value = false;
    return;
  }

  try {
    await ITeroService.oauthCallback({}, { state, code });
    await queryClient.invalidateQueries({ queryKey: ITeroQueryKeys.status() });
    success.value = true;
    startRedirectToSettings();
  } catch (err) {
    error.value = parseErrorMessage(err);
  } finally {
    loading.value = false;
  }
}

onMounted(submit);

onBeforeUnmount(() => {
  if (redirectTimer) {
    clearInterval(redirectTimer);
  }
});
</script>

<template>
  <v-container class="d-flex align-center justify-center fill-height">
    <v-card
      v-if="success"
      max-width="400"
      class="pa-6 text-center tw:overflow-visible"
    >
      <div
        class="tw:absolute tw:left-1/2 tw:top-0 tw:z-content-overlay tw:-translate-x-1/2 tw:-translate-y-1/2"
      >
        <div
          class="tw:flex tw:h-18 tw:w-18 tw:items-center tw:justify-center tw:rounded-full tw:shadow-lg"
          :style="{
            backgroundColor: 'rgb(var(--v-theme-secondary))',
          }"
        >
          <v-icon
            icon="mdi-check-circle-outline"
            color="white"
            size="40"
          />
        </div>
      </div>
      <v-card-title class="mt-8">
        <img
          :src="logoITero"
          alt="iTero"
          class="tw:h-5.5"
        />
        {{ t("features.integrations.iteroOAuth.successTitle") }}
      </v-card-title>
      <v-card-text class="tw:mb-6">
        {{
          t("features.integrations.iteroOAuth.successText", {
            seconds: redirectCountdown,
          })
        }}
      </v-card-text>
    </v-card>

    <v-card
      v-else-if="error"
      max-width="400"
      class="pa-6 text-center tw:overflow-visible"
    >
      <div
        class="tw:absolute tw:left-1/2 tw:top-0 tw:z-content-overlay tw:-translate-x-1/2 tw:-translate-y-1/2"
      >
        <div
          class="tw:flex tw:h-18 tw:w-18 tw:items-center tw:justify-center tw:rounded-full tw:shadow-lg"
          :style="{
            backgroundColor: 'rgb(var(--v-theme-error))',
          }"
        >
          <v-icon
            icon="mdi-alert-circle-outline"
            color="white"
            size="40"
          />
        </div>
      </div>
      <v-card-title class="mt-8">{{
        t("features.integrations.iteroOAuth.failureTitle")
      }}</v-card-title>
      <v-card-text class="tw:mb-6">{{ error }}</v-card-text>
      <v-btn
        elevation="0"
        color="primary"
        :loading="loading"
        @click="submit"
        >{{ t("features.integrations.iteroOAuth.retry") }}</v-btn
      >
    </v-card>

    <div
      v-else
      class="text-center"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
      />
      <p class="mt-4 text-medium-emphasis">
        {{ t("features.integrations.iteroOAuth.processing") }}
      </p>
    </div>
  </v-container>
</template>
